import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Landmark } from 'lucide-react';
import { toast } from 'sonner';
import {
  ORGANIZATION_SUBSCRIPTION_TIERS,
  SUBSCRIPTION_TIER_LABELS,
  hasEnterpriseIdentityTier,
  type OrganizationSubscriptionTier,
} from '@reflex/shared';
import { Badge } from '@reflex/ui/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { ConfirmDialog } from '@reflex/ui/components/shared/ConfirmDialog';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { AdminBillingEntityDomainClaimsSection } from '@/components/admin/billing-entities/AdminBillingEntityDomainClaimsSection';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ROUTES } from '@/lib/routes';

export function AdminBillingEntityDetailPage() {
  const { billingEntityId } = useParams<{ billingEntityId: string }>();
  const queryClient = useQueryClient();
  const [pendingTier, setPendingTier] = useState<OrganizationSubscriptionTier | null>(null);
  const detailQuery = useQuery({
    queryKey: queryKeys.adminBillingEntity(billingEntityId ?? ''),
    queryFn: () => api.getAdminBillingEntity(billingEntityId ?? ''),
    enabled: Boolean(billingEntityId),
  });

  const tierMutation = useMutation({
    mutationFn: (subscriptionTier: OrganizationSubscriptionTier) =>
      api.updateAdminBillingEntity(billingEntityId ?? '', { subscriptionTier }),
    onSuccess: (billingEntity) => {
      setPendingTier(null);
      toast.success(
        `Subscription tier changed to ${SUBSCRIPTION_TIER_LABELS[billingEntity.subscriptionTier]}`,
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminBillingEntity(billingEntity.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminBillingEntities });
    },
    onError: (error) => {
      toast.error('Could not change the subscription tier', {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  if (!billingEntityId) return <Navigate to={ROUTES.admin.companies} replace />;

  const detail = detailQuery.data;

  function selectTier(next: OrganizationSubscriptionTier): void {
    if (
      detail &&
      hasEnterpriseIdentityTier(detail.billingEntity.subscriptionTier) &&
      !hasEnterpriseIdentityTier(next)
    ) {
      setPendingTier(next);
      return;
    }
    tierMutation.mutate(next);
  }

  return (
    <AdminSubPageLayout
      title={detail?.billingEntity.name ?? 'Company'}
      description="Company-level subscription, organizations, and verified email domains."
      icon={<Landmark />}
    >
      <AsyncContent
        isLoading={detailQuery.isLoading}
        isError={detailQuery.isError}
        errorMessage="Failed to load the company."
      >
        {detail && (
          <>
            <SectionCard
              title="Company"
              description="The subscription tier shared by every organization this company owns."
              icon={<Landmark />}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-2">
                  <div>
                    <SectionLabel>Name</SectionLabel>
                    <p>{detail.billingEntity.name}</p>
                  </div>
                  <div>
                    <SectionLabel>Organizations</SectionLabel>
                    {detail.organizations.length === 0 ? (
                      <EmptyState
                        variant="bordered"
                        title="No organizations"
                        description="Attach an organization from its admin detail page."
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {detail.organizations.map((organization) => (
                          <Badge key={organization.id} variant="secondary" asChild>
                            <Link to={ROUTES.admin.organization(organization.id)}>
                              {organization.name} ({organization.slug})
                            </Link>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <Select
                  value={detail.billingEntity.subscriptionTier}
                  onValueChange={(value) => selectTier(value as OrganizationSubscriptionTier)}
                  disabled={tierMutation.isPending}
                >
                  <SelectTrigger className="w-44" aria-label="Company subscription tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORGANIZATION_SUBSCRIPTION_TIERS.map((tier) => (
                      <SelectItem key={tier} value={tier}>
                        {SUBSCRIPTION_TIER_LABELS[tier]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </SectionCard>
            <AdminBillingEntityDomainClaimsSection billingEntity={detail.billingEntity} />
            <ConfirmDialog
              open={pendingTier !== null}
              onOpenChange={(open) => {
                if (!open) setPendingTier(null);
              }}
              title="Change subscription tier?"
              description={`${detail.billingEntity.name} owns ${detail.organizations.length} ${detail.organizations.length === 1 ? 'organization' : 'organizations'}. Moving them to ${pendingTier ? SUBSCRIPTION_TIER_LABELS[pendingTier] : ''} stops enterprise identity shortly. Existing settings are kept and reactivate if the company returns to Enterprise.`}
              confirmLabel="Change tier"
              onConfirm={() => {
                if (pendingTier) tierMutation.mutate(pendingTier);
              }}
            />
          </>
        )}
      </AsyncContent>
    </AdminSubPageLayout>
  );
}
